import { type FC, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useDebounce } from 'react-use';
import InputSelect from '@components/InputSelect';
import { SearchableInputSelectOpenSearch } from '@components/SearchableInputSelectOpenSearch';
import type { UIInputValue } from '@components/types';
import { EXCLUDE_PRE_DRAFTS_FILTERS } from '@constants/statuses';
import type { ResourceState } from '@domain/schemas/contract.schema';
import { useBrandingOptions } from '@features/create-entity-modal';
import { useSubmissionsOpensearch } from '@queries/fetchSubmissions';
import type { TemplateShort } from '@queries/fetchTemplates';
import { useTemplates } from '@queries/fetchTemplates';
import { isEmptyValue } from '@root/helpers';
import type { WithRequired } from '@root/helpers/object/types';
import type { SubmissionShort } from '@src/queries';

import type { StepProps } from '../../types';

interface PublishedTemplate extends Omit<WithRequired<TemplateShort, 'name'>, 'status'> {
  status: ResourceState.PUBLISHED;
}

const filterPublishedTemplates = (item: TemplateShort): item is PublishedTemplate => {
  return !isEmptyValue(item.name) && item.status === 'PUBLISHED';
};

const filterSubmissions = (item: SubmissionShort) => {
  return !isEmptyValue(item.name);
};

export const Step2OpenSearch: FC<StepProps> = ({ control, formValues, setValue }) => {
  'use memo';

  const { brandingFieldName, isLoading: isFetchingBranding, onCopyBrandingFromResource } = useBrandingOptions();
  const { data: templates = [], isLoading: isFetchingTemplates } = useTemplates();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useDebounce(() => setDebouncedSearch(searchText), 300, [searchText]);

  const {
    data: submissionsPaginated,
    isFetching: isFetchingSubmissions,
    isFetchingNextPage: isFetchingSubmissionsNextPage,
    fetchNextPage: fetchSubmissionsNextPage,
    hasNextPage: hasNextSubmissionsPage,
  } = useSubmissionsOpensearch({
    search: debouncedSearch,
    queryFilters: EXCLUDE_PRE_DRAFTS_FILTERS,
  });

  const submissions = submissionsPaginated?.pages.flatMap((page) => page.data) ?? [];

  const handleCopyFromEntityIdChange = (entityId: UIInputValue, mode: 'submissions' | 'templates') => {
    const data = mode === 'submissions' ? submissions : templates;
    const entity = data.find((item) => item.id === entityId);

    const result = onCopyBrandingFromResource(entity);

    if (result) {
      setValue(brandingFieldName, result);
    }
  };

  const parsedTemplates = templates
    .filter(filterPublishedTemplates)
    .map((item) => ({ name: item.name, value: item.id }));

  const submissionOptions = submissions.filter(filterSubmissions).map((item) => ({ name: item.name, value: item.id }));

  const isLoading =
    (isFetchingSubmissions && !isFetchingSubmissionsNextPage) || isFetchingTemplates || isFetchingBranding;

  const disableTemplatesPicker = !!formValues?.copy_from_submission || isLoading;
  const disableSubmissionsPicker = !!formValues?.copy_from_template || isLoading;

  return (
    <div className="flex flex-col">
      <Controller
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InputSelect
            {...field}
            errors={error?.message}
            isClearable
            isDisabled={disableTemplatesPicker}
            isLoading={isLoading}
            isSearchable
            menuOuterClassName="max-w-lg"
            onChange={(value) => {
              handleCopyFromEntityIdChange(value, 'templates');
              return field.onChange(value);
            }}
            labelText="Select a template"
            options={parsedTemplates}
            placeholder="Search"
            value={formValues?.copy_from_template}
            dataTestId="select:copy_from_template"
            shouldRenderInsidePortal
          />
        )}
        name="copy_from_template"
      />

      <div className="relative flex items-center py-5 text-gray-500">
        <div className="grow border-t border-gray-300"></div>
        <span className="mx-2 shrink text-sm leading-5 font-normal">Or</span>
        <div className="grow border-t border-gray-300"></div>
      </div>

      <Controller
        control={control}
        render={({ field: { ref: _ref, ...props }, fieldState: { error } }) => (
          <SearchableInputSelectOpenSearch
            {...props}
            isFetchingNextPage={isFetchingSubmissionsNextPage}
            fetchNextPage={fetchSubmissionsNextPage}
            hasNextPage={hasNextSubmissionsPage}
            searchText={searchText}
            setSearchText={setSearchText}
            errors={error?.message}
            isDisabled={disableSubmissionsPicker}
            isLoading={isLoading}
            noOptionsLeftLabel="No existing contracts available"
            onChange={(value) => {
              handleCopyFromEntityIdChange(value, 'submissions');
              return props.onChange(value);
            }}
            labelText="Duplicate an existing contract"
            placeholder="Search contracts"
            options={submissionOptions}
            // Getting the value from `formValues` as opposed to `Controller` since it does a weird caching which breaks the `Clear selection` logic
            value={formValues?.copy_from_submission}
            dataTestId="select:copy_from_submission"
          />
        )}
        name="copy_from_submission"
      />
    </div>
  );
};
